import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { exportCSV, parseDecimal } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, FileDown, Printer, Search } from "lucide-react";
import CategorySelect from "@/components/CategorySelect";
import DirectoryPicker from "@/components/DirectoryPicker";

export default function CrudPage({
  title, subtitle, endpoint, columns, fields, testid,
  computeRow = (r) => r, emptyDefaults = {}, headerAccent = "text-slate-900",
  filterOptions = null, summary = null, rowActions = [], groupBy = null, sortRows = null,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterValue, setFilterValue] = useState("Sve");
  const [dynOpts, setDynOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDefaults);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${endpoint}`);
      setRows(res.data.map(computeRow));
    } catch (e) {
      toast.error("Greška pri učitavanju podataka");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  useEffect(() => {
    if (filterOptions && filterOptions.type) {
      api.get("/categories", { params: { type: filterOptions.type } }).then((r) => setDynOpts(r.data)).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [filterOptions && filterOptions.type]);

  const filtered = useMemo(() => {
    let out = rows;
    if (filterOptions && filterValue !== "Sve") {
      out = out.filter((r) => r[filterOptions.key] === filterValue);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((r) =>
        columns.some((c) => {
          const v = c.export ? c.export(r) : r[c.key];
          return String(v ?? "").toLowerCase().includes(q);
        })
      );
    }
    return out;
  }, [rows, query, columns, filterOptions, filterValue]);

  const toDisp = (base) => {
    const o = { ...base };
    fields.forEach((f) => { if (f.type === "number" && o[f.key] !== undefined && o[f.key] !== null && o[f.key] !== "") o[f.key] = String(o[f.key]).replace(".", ","); });
    return o;
  };
  const openAdd = () => { setEditing(null); setForm(toDisp(emptyDefaults)); setOpen(true); };
  const openEdit = (row) => {
    const base = {};
    fields.forEach((f) => (base[f.key] = row[f.key] ?? ""));
    setEditing(row); setForm(toDisp(base)); setOpen(true);
  };

  const save = async () => {
    for (const f of fields) {
      if (f.required && (form[f.key] === "" || form[f.key] === undefined || form[f.key] === null)) {
        toast.error(`Polje "${f.label}" je obavezno`);
        return;
      }
    }
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number") payload[f.key] = parseDecimal(payload[f.key]); });
    try {
      if (editing) {
        await api.put(`/${endpoint}/${editing.id}`, payload);
        toast.success("Zapis ažuriran");
      } else {
        await api.post(`/${endpoint}`, payload);
        toast.success("Zapis dodan");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Greška pri spremanju");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${deleteId}`);
      toast.success("Zapis obrisan");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Greška pri brisanju");
    }
  };

  const doQuickAction = async (row, action) => {
    try {
      await api.put(`/${endpoint}/${row.id}`, action.patch(row));
      toast.success(action.successText || "Ažurirano");
      load();
    } catch (e) {
      toast.error("Greška pri ažuriranju");
    }
  };

  const handleExport = () => {
    const headers = columns.map((c) => c.label);
    const data = filtered.map((r) => columns.map((c) => (c.export ? c.export(r) : r[c.key] ?? "")));
    exportCSV(endpoint, headers, data);
    toast.success("Excel (CSV) preuzet");
  };

  const renderDataRow = (row) => (
    <TableRow key={row.id} data-testid={`${testid}-row`}>
      {columns.map((c) => (
        <TableCell key={c.key} className="whitespace-nowrap">{c.render ? c.render(row) : (row[c.key] ?? "—")}</TableCell>
      ))}
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          {rowActions.filter((a) => (a.visible ? a.visible(row) : true)).map((a) => (
            <Button
              key={a.key}
              variant="outline"
              size="sm"
              className={`h-8 ${a.tone === "success" ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300" : ""}`}
              onClick={() => doQuickAction(row, a)}
              data-testid={`${testid}-${a.key}-btn`}
            >
              {a.icon ? <a.icon className="h-4 w-4 mr-1" /> : null}{a.label}
            </Button>
          ))}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid={`${testid}-edit-btn`}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDeleteId(row.id)} data-testid={`${testid}-delete-btn`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const ordered = sortRows ? [...filtered].sort(sortRows) : filtered;
  const grouped = {};
  if (groupBy) ordered.forEach((r) => { const k = r[groupBy] || "Ostalo"; (grouped[k] = grouped[k] || []).push(r); });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className={`text-2xl sm:text-3xl font-extrabold font-heading tracking-tight ${headerAccent} dark:text-slate-100`}>{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid={`${testid}-export-excel`}>
            <FileDown className="h-4 w-4 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid={`${testid}-export-pdf`}>
            <Printer className="h-4 w-4 mr-1.5" /> Print / PDF
          </Button>
          <Button size="sm" onClick={openAdd} data-testid={`${testid}-add-btn`}>
            <Plus className="h-4 w-4 mr-1.5" /> Novi unos
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pretraži..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid={`${testid}-search`}
          />
        </div>
        {filterOptions && (
          <Select value={filterValue} onValueChange={setFilterValue}>
            <SelectTrigger className="w-full sm:w-56" data-testid={`${testid}-filter`}>
              <SelectValue placeholder={filterOptions.label} />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="Sve">Svi ({filterOptions.label})</SelectItem>
              {(filterOptions.options || dynOpts).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {summary && summary(filtered)}

      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                {columns.map((c) => (
                  <TableHead key={c.key} className="whitespace-nowrap font-semibold text-slate-700 dark:text-slate-200">{c.label}</TableHead>
                ))}
                <TableHead className="text-right w-[90px]">Akcije</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={columns.length + 1} className="h-24 text-center text-muted-foreground">Učitavanje...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length + 1} className="h-24 text-center text-muted-foreground">Nema zapisa. Kliknite "Novi unos".</TableCell></TableRow>
              ) : groupBy ? (
                Object.entries(grouped).map(([g, rows], gi) => (
                  <React.Fragment key={g}>
                    {gi > 0 && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={columns.length + 1} className="p-0 h-4 border-0 bg-transparent" />
                      </TableRow>
                    )}
                    <TableRow className="hover:bg-transparent border-0">
                      <TableCell colSpan={columns.length + 1} className="bg-primary/10 text-primary font-heading font-bold text-sm py-2.5 rounded-sm" data-testid={`${testid}-group-${g}`}>
                        {g} <span className="text-muted-foreground font-normal">· {rows.length}</span>
                      </TableCell>
                    </TableRow>
                    {rows.map(renderDataRow)}
                  </React.Fragment>
                ))
              ) : (
                ordered.map(renderDataRow)
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Uredi zapis" : "Novi unos"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
                <Label htmlFor={f.key} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{f.label}</Label>
                {f.type === "category" ? (
                  <CategorySelect type={f.categoryType} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testid={`${testid}-field-${f.key}`} />
                ) : f.type === "directory" ? (
                  <DirectoryPicker
                    endpoint={f.directory.endpoint}
                    placeholder={f.directory.placeholder}
                    groupBy={f.directory.groupBy}
                    getLabel={f.directory.getLabel}
                    createFields={f.directory.createFields}
                    onPick={(item) => {
                      const patch = f.directory.map(item);
                      Object.keys(patch).forEach((k) => { if (patch[k] === undefined || patch[k] === null) delete patch[k]; });
                      setForm({ ...form, ...patch });
                    }}
                    testid={`${testid}-field-${f.key}`}
                  />
                ) : f.type === "checkbox" ? (
                  <div className="flex items-center gap-2 pt-1">
                    <input id={f.key} type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} data-testid={`${testid}-field-${f.key}`} className="h-4 w-4 rounded border-input accent-primary" />
                    <span className="text-sm text-muted-foreground">{f.checkboxLabel || "Da"}</span>
                  </div>
                ) : f.type === "textarea" ? (
                  <Textarea id={f.key} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testid}-field-${f.key}`} />
                ) : f.type === "select" ? (
                  <Select value={form[f.key] ? String(form[f.key]) : ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`${testid}-field-${f.key}`}><SelectValue placeholder="Odaberi..." /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={f.key}
                    type={f.type === "number" ? "text" : (f.type || "text")}
                    inputMode={f.type === "number" ? "decimal" : undefined}
                    value={form[f.key] ?? ""}
                    placeholder={f.type === "number" ? (f.placeholder || "npr. 100,10") : f.placeholder}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? e.target.value.replace(/[^0-9.,-]/g, "") : e.target.value })}
                    data-testid={`${testid}-field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testid}-cancel-btn`}>Odustani</Button>
            <Button onClick={save} data-testid={`${testid}-save-btn`}>Spremi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Obrisati zapis?</AlertDialogTitle>
            <AlertDialogDescription>Ova radnja se ne može poništiti.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-delete-cancel`}>Odustani</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`${testid}-delete-confirm`}>Obriši</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function Badge({ tone = "muted", children }) {
  const map = {
    muted: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800",
    warning: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    danger: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800",
    info: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800",
  };
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${map[tone] || map.muted}`}>{children}</span>;
}
